import React, { useState } from 'react';
import { Btn, Msg, Section, panelPost } from '../kit';
import { Labeled, inputCls } from '../email/emailKit';
import {
  type Note, PreviewFrame, S, SaveBar, type StoriesSetting, type StoryRow, type TabProps, Toggle, move, nextKey,
  useDraft,
} from './shared';

/**
 * Success stories (was "Cosmic Connections") — the matchmaking rows on the
 * homepage: who · how · outcome, under a title and a subtitle. POST
 * homepage/save_stories replaces the rows (order = display order). The public
 * /homepage/content is cached for 30 s, so a save shows within half a minute.
 */

type RowDraft = StoryRow & { key: string };
type Draft = { enabled: boolean; title: string; subtitle: string; rows: RowDraft[] };

const draftFrom = (s: StoriesSetting): Draft => ({
  enabled: s?.enabled !== false,
  title: s?.title || '',
  subtitle: s?.subtitle || '',
  rows: (Array.isArray(s?.rows) ? s.rows : []).map(r => ({
    from: String(r.from || ''), verb: String(r.verb || ''), to: String(r.to || ''), key: nextKey(),
  })),
});

/** homepage_content.save_stories' field lengths — mirrored so the inputs stop there. */
const LEN = { title: 120, subtitle: 200, from: 80, verb: 60, to: 80 };

/** The section as the homepage draws it on a desktop: the title's first word
 *  accented, the subtitle, then each row with the dotted connector. */
const StoriesPreview: React.FC<{ d: Draft }> = ({ d }) => {
  const title = d.title.trim();
  const cut = title.indexOf(' ');
  const first = cut > 0 ? title.slice(0, cut) : title;
  const rest = cut > 0 ? title.slice(cut) : '';
  const rows = d.rows.filter(r => r.from.trim() || r.verb.trim() || r.to.trim());
  return (
    <PreviewFrame note={d.enabled ? undefined : 'switched off — the homepage does not show the section'} dim={!d.enabled}>
      <div className="text-center mb-5">
        <h4 className="text-lg sm:text-2xl font-bold text-grayscale-1300">
          <span className="text-primary-600">{first || '—'}</span>{rest}
        </h4>
        {d.subtitle.trim() && <p className="mt-1 text-sm text-grayscale-1100">{d.subtitle}</p>}
      </div>
      <div className="max-w-3xl mx-auto flex flex-col gap-3">
        {rows.map(r => (
          <div key={r.key} className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 text-sm text-center">
            <span className="text-grayscale-1300 text-right">{r.from}</span>
            <span className="flex items-center gap-1.5 text-primary-600 italic text-xs">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-500 shrink-0" />
              <span className="w-6 sm:w-10 border-t-2 border-dotted border-primary-400" />
              <span className="whitespace-nowrap">{r.verb}</span>
              <span className="w-6 sm:w-10 border-t-2 border-dotted border-primary-400" />
              <span className="w-1.5 h-1.5 rounded-full bg-primary-500 shrink-0" />
            </span>
            <span className="text-grayscale-1300 text-left">{r.to}</span>
          </div>
        ))}
        {!rows.length && <div className="text-sm text-grayscale-1100 text-center">No rows yet.</div>}
      </div>
    </PreviewFrame>
  );
};

const SuccessStories: React.FC<TabProps> = ({ data, onSaved, onDirty }) => {
  const { draft, edit, dirty, discard, settle } = useDraft(data.settings.stories, draftFrom, onDirty);
  const [busy, setBusy] = useState<'' | 'save' | 'reset'>('');
  const [msg, setMsg] = useState<Note>(null);
  const maxRows = data.limits?.rows || 20;

  const setField = (patch: Partial<Draft>) => edit(d => ({ ...d, ...patch }));
  const setRow = (key: string, patch: Partial<RowDraft>) =>
    edit(d => ({ ...d, rows: d.rows.map(r => (r.key === key ? { ...r, ...patch } : r)) }));
  const moveRow = (i: number, dir: -1 | 1) => edit(d => ({ ...d, rows: move(d.rows, i, dir) }));
  const removeRow = (key: string) => edit(d => ({ ...d, rows: d.rows.filter(r => r.key !== key) }));
  const addRow = () => edit(d => ({ ...d, rows: [...d.rows, { from: '', verb: '', to: '', key: nextKey() }] }));

  const save = async () => {
    if (!draft.title.trim()) { setMsg({ kind: 'error', text: 'The section needs a title.' }); return; }
    if (draft.rows.length > maxRows) { setMsg({ kind: 'error', text: `At most ${maxRows} rows.` }); return; }
    if (draft.enabled && !draft.rows.length) {
      setMsg({ kind: 'error', text: 'Add at least one row, or switch the section off.' });
      return;
    }
    const gap = draft.rows.findIndex(r => !r.from.trim() || !r.verb.trim() || !r.to.trim());
    if (gap >= 0) {
      setMsg({ kind: 'error', text: `Row ${gap + 1} needs all three parts (left, middle and right).` });
      return;
    }
    setBusy('save'); setMsg(null);
    const { data: next, error } = await panelPost(S, 'save_stories', {
      enabled: draft.enabled, title: draft.title.trim(), subtitle: draft.subtitle.trim(),
      rows: draft.rows.map(r => ({ from: r.from.trim(), verb: r.verb.trim(), to: r.to.trim() })),
    });
    setBusy('');
    if (error || !next) { setMsg({ kind: 'error', text: error || 'Save failed' }); return; }
    onSaved(next);
    settle();
    setMsg({ kind: 'ok', text: 'Saved — the homepage picks it up within half a minute.' });
  };

  const reset = async () => {
    setBusy('reset'); setMsg(null);
    const { data: next, error } = await panelPost(S, 'reset', { section: 'stories' });
    setBusy('');
    if (error || !next) { setMsg({ kind: 'error', text: error || 'Reset failed' }); return; }
    onSaved(next);
    settle();
    setMsg({ kind: 'ok', text: 'Back to the built-in title, subtitle and eight rows.' });
  };

  const saved = data.settings.stories;
  const nowLine = !saved?.enabled ? 'the section is switched off, so the homepage does not show it.'
    : `“${saved.title}” with ${(saved.rows || []).length} row${(saved.rows || []).length === 1 ? '' : 's'}.`;

  return (
    <Section title="Success stories">
      <p className="text-xs text-grayscale-1100 -mt-1">
        The matchmaking rows below the Trusted-by banner: who (left) · how (middle) · the outcome (right),
        shown in this order.
      </p>
      <div className="text-xs text-grayscale-1200">On the homepage now: {nowLine}</div>
      {msg && <Msg kind={msg.kind}>{msg.text}</Msg>}

      <div className="flex items-center gap-2">
        <Toggle on={draft.enabled} onChange={v => setField({ enabled: v })} label="Show the section" />
        <span className="text-sm text-grayscale-1200">Show the section</span>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <Labeled label="Title" hint="the first word is accented">
          <input value={draft.title} maxLength={LEN.title} onChange={e => setField({ title: e.target.value })}
            placeholder="Success Stories" className={inputCls} />
        </Labeled>
        <Labeled label="Subtitle">
          <input value={draft.subtitle} maxLength={LEN.subtitle} onChange={e => setField({ subtitle: e.target.value })}
            placeholder="High velocity discovery and matchmaking in action" className={inputCls} />
        </Labeled>
      </div>

      <div className="flex flex-col gap-2">
        <div className="text-xs text-grayscale-1100">
          Rows — {draft.rows.length} · at most {maxRows} · left · middle · right
        </div>
        {draft.rows.map((r, i) => (
          <div key={r.key} className="flex items-center gap-2 flex-wrap lg:flex-nowrap rounded-lg border border-grayscale-300 p-2">
            <span className="text-xs text-grayscale-1100 w-5 text-right shrink-0">{i + 1}</span>
            <div className="flex-1 min-w-[10rem]">
              <input value={r.from} maxLength={LEN.from} aria-label={`Row ${i + 1} left`}
                onChange={e => setRow(r.key, { from: e.target.value })}
                placeholder="Who — e.g. Mid Sized Medtech Startup" className={inputCls} />
            </div>
            <span className="text-grayscale-1100 shrink-0">·</span>
            <div className="flex-1 min-w-[9rem] lg:max-w-[15rem]">
              <input value={r.verb} maxLength={LEN.verb} aria-label={`Row ${i + 1} middle`}
                onChange={e => setRow(r.key, { verb: e.target.value })}
                placeholder="How — e.g. Co-Development" className={`${inputCls} italic`} />
            </div>
            <span className="text-grayscale-1100 shrink-0">·</span>
            <div className="flex-1 min-w-[10rem]">
              <input value={r.to} maxLength={LEN.to} aria-label={`Row ${i + 1} right`}
                onChange={e => setRow(r.key, { to: e.target.value })}
                placeholder="Outcome — e.g. 17 Technologies Shortlisted" className={inputCls} />
            </div>
            <span className="flex items-center gap-1 shrink-0">
              <Btn small onClick={() => moveRow(i, -1)} disabled={i === 0} title="Move up">↑</Btn>
              <Btn small onClick={() => moveRow(i, 1)} disabled={i === draft.rows.length - 1} title="Move down">↓</Btn>
              <Btn small kind="danger" onClick={() => removeRow(r.key)} title="Delete this row (Discard brings it back)">
                Delete
              </Btn>
            </span>
          </div>
        ))}
        <div>
          <Btn small onClick={addRow} disabled={draft.rows.length >= maxRows}
            title={draft.rows.length >= maxRows ? `At most ${maxRows} rows` : undefined}>
            + Add row
          </Btn>
        </div>
      </div>

      <StoriesPreview d={draft} />

      <SaveBar dirty={dirty} busy={busy} onSave={save} onDiscard={() => { discard(); setMsg(null); }} onReset={reset} />
    </Section>
  );
};

export default SuccessStories;
